import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "primeicons/primeicons.css"; // PrimeIcons
import "primereact/resources/primereact.min.css"; // PrimeReact styles
import { PrimeReactProvider } from "primereact/api";
import { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSelector } from "react-redux";
import "./App.css";
import "primeflex/primeflex.css";

import type { RootState } from "./Redux/Reducers/RootReducer";
import { AppRoutes } from "./routes";
import { ThemeProvider } from "./Theme/ThemeContext";

const BrowserHistoryGuard = () => {
  const error = useSelector((state: RootState) => state.Error.error);
  const location = useLocation();
  const navigate = useNavigate();
  const isBlockingNavigation = !!error;
  const currentPathRef = useRef(
    `${location.pathname}${location.search}${location.hash}`,
  );

  useEffect(() => {
    if (!isBlockingNavigation) {
      return;
    }

    currentPathRef.current = `${location.pathname}${location.search}${location.hash}`;
    // While an error popup is visible, keep a synthetic entry so browser back/forward is ignored.
    globalThis.history.pushState(
      { noBrowserNavigation: true },
      "",
      globalThis.location.href,
    );
  }, [isBlockingNavigation, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isBlockingNavigation) {
      return;
    }

    const handlePopState = () => {
      navigate(currentPathRef.current, { replace: true });
      globalThis.history.pushState(
        { noBrowserNavigation: true },
        "",
        globalThis.location.href,
      );
    };

    globalThis.addEventListener("popstate", handlePopState);
    return () => {
      globalThis.removeEventListener("popstate", handlePopState);
    };
  }, [isBlockingNavigation, navigate]);

  return null;
};

function App() {
  return (
    <PrimeReactProvider>
      <ThemeProvider>
        <Router>
          <BrowserHistoryGuard />
          <AppRoutes />
        </Router>
      </ThemeProvider>
    </PrimeReactProvider>
  );
}

export default App;
